import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import { useRef, useState } from "react";
import { useToast } from "../../../../shared/hooks";
import { SinisterDocumentsService } from "../../../../shared/services/api/sinister/SinisterDocumentsService";

interface ModalAddImageValueProps {
	onSuccessUpload?: () => void;
	isDisabled?: boolean;
	sinisterId: string;
	checkListId: string;
	documentTypeId: string;
}

export const ModalAddImageValue = ({ onSuccessUpload, isDisabled = false, sinisterId, checkListId, documentTypeId }: ModalAddImageValueProps) => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [showUploader, setShowUploader] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [isUploading, setIsUploading] = useState(false);
	const toastHandler = useToast();

	const allowedTypes = [
		"image/png",
		"image/jpeg",
		"application/pdf",
		"application/msword",
		"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
		"application/vnd.oasis.opendocument.text",
	];

	const maxFileSize = 50 * 1024 * 1024; // 50MB

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			if (!allowedTypes.includes(file.type)) {
				setErrorMessage("Tipo de arquivo não suportado.");
				e.target.value = "";
				return;
			}

			if (file.size > maxFileSize) {
				setErrorMessage("O arquivo excede o tamanho máximo de 50MB.");
				e.target.value = "";
				return;
			}

			setSelectedFile(file);
			setErrorMessage(null);
		}
	};

	const handleConfirm = async () => {
		if (selectedFile) {
			setIsUploading(true);
			try {
				await SinisterDocumentsService.uploadSinisterValuesDocument(sinisterId, checkListId, documentTypeId, selectedFile);

				toastHandler.present({
					type: "success",
					messageOrContent: "Arquivo enviado com sucesso!",
					position: "top-center",
				});

				setSelectedFile(null);
				setShowUploader(false);
				setErrorMessage(null);
				onSuccessUpload?.();
			} catch (e: any) {
				toastHandler.present({
					type: "error",
					messageOrContent: e.message,
					position: "top-center",
				});
			} finally {
				setIsUploading(false);
			}
		}
	};

	const handleRemove = () => {
		setSelectedFile(null);
		setShowUploader(false);
		setErrorMessage(null);
	};

	if (!showUploader) {
		return (
			<Box textAlign="center">
				<Button variant="outlined" startIcon={<AddIcon />} onClick={() => setShowUploader(true)} disabled={isDisabled}>
					Adicionar Anexo
				</Button>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				border: "2px dashed orange",
				borderRadius: 2,
				p: 3,
				textAlign: "center",
				bgcolor: "#fff",
			}}
		>
			{!selectedFile ? (
				<Box onClick={() => fileInputRef.current?.click()} sx={{ cursor: "pointer" }}>
					<Typography variant="caption" color="text.secondary">
						Clique para realizar o carregamento do arquivo
					</Typography>

					<Box my={2}>
						<Typography fontWeight={700}>Tamanho máximo:</Typography>
						<Typography variant="caption">50.00MB</Typography>
					</Box>

					<CloudUploadIcon sx={{ fontSize: 48, mb: 1 }} />

					<Typography fontWeight={700}>Tipos suportados:</Typography>
					<Typography variant="caption">PNG, JPG, JPEG, DOC, DOCX, PDF, ODT</Typography>

					<input ref={fileInputRef} type="file" hidden accept=".png,.jpg,.jpeg,.doc,.docx,.pdf,.odt" onChange={handleFileChange} />

					{errorMessage && (
						<Box mt={2}>
							<Alert severity="error">{errorMessage}</Alert>
						</Box>
					)}
				</Box>
			) : (
				<Stack spacing={2} alignItems="center">
					<Typography variant="subtitle2" fontWeight={600}>
						Arquivo selecionado:
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{selectedFile.name}
					</Typography>

					<Stack direction="row" spacing={2}>
						<Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleRemove} disabled={isUploading}>
							Remover
						</Button>
						<Button variant="contained" color="primary" startIcon={<CheckCircleIcon />} onClick={handleConfirm} disabled={isUploading}>
							Confirmar
						</Button>
					</Stack>
				</Stack>
			)}
		</Box>
	);
};
